import {
  IntakeDraftSectionsSchema,
  IntakeSubmissionSchema,
  INTAKE_HONEYPOT_FIELD,
  INTAKE_MAX_BODY_BYTES,
  MAX_INTAKE_ROWS,
  INTAKE_ROW_ID_PATTERN,
  INTAKE_ESTATE_DOCUMENTS,
  INTAKE_ASSET_JOBS,
  propertyAsks,
  asksOtherOwnersDetails,
  asksBusinessHoldingsDetails,
  aboutYouWithoutInvitedEmail,
  submissionWithoutInvitedEmail,
} from '@steward-app/steward-types';

/**
 * The wizard's working copy of a client intake (docs/25 §3, S4).
 *
 * The draft is deliberately NOT `IntakeSubmission`: a half-filled form holds empty
 * strings where the schema wants a required value or an enum member, and every field
 * has to be a controlled input from the first keystroke. So the draft is all-strings,
 * and `toSubmission` turns it into the shape the schema validates: trimmed, blank
 * optional fields dropped, blank repeat rows dropped.
 *
 * `@steward-app/steward-types` stays the only authority on what is valid — this file
 * never restates a rule, a length, or an option list. It converts, and it asks.
 */

/**
 * The `maxLength` the inputs enforce, read off the schema rather than retyped: the
 * bounds live in `intakeSchema.ts` and a change there moves the inputs with it. Three
 * representative fields stand in for the schema's three string kinds (`short`,
 * `amount`, `longText`), which is all the wizard needs to know about lengths.
 */
const shape = IntakeSubmissionSchema.shape;
export const MAX_SHORT = shape.aboutYou.shape.firstName.unwrap().maxLength ?? 200;
export const MAX_AMOUNT = shape.incomeAndGoals.shape.monthlyExpenses.unwrap().maxLength ?? 100;
export const MAX_LONG = shape.incomeAndGoals.shape.notes.unwrap().maxLength ?? 5000;
/**
 * The one field bound WIDER than `MAX_SHORT` (fix/intake-gate-copy, 2026-09-19):
 * `person.shape.email` is `z.email().max(254)`, not the 200-character `short` every
 * other field on this shape uses, and `aboutYou`/`spousePartner.spouse` share that
 * same `person` schema — so one constant, read off `aboutYou`, covers both call
 * sites. (`IntakeBasics.tsx`'s email input carries the same 254 as a literal.)
 *
 * ⚠️ **Why this matters is NOT the same reason for both call sites — say so precisely,
 * because the two look identical at the call site and are not.** Only About You compares
 * the address typed here against the one the prospect already verified at the gate, for
 * equality (`emailMatchesVerified`, plumbed to `AboutYouSection` alone —
 * `sections/index.tsx`'s own comment: "Only `aboutYou`'s render"). Before this constant,
 * About You's input was capped at `MAX_SHORT` (200), so an address of 201-254
 * characters — legal at the gate, legal to the schema — could be typed at the gate and
 * then could never be re-typed identically there: a silent, unrecoverable dead end at
 * the one point in the flow that checks the two match. Spouse/Partner's email is
 * compared against nothing; nobody verifies a spouse's address ahead of this field, so
 * there is no dead end to close there. It moves to `MAX_EMAIL` anyway for a narrower
 * reason: it is the SAME `person` schema, so the schema already allows 254 there too,
 * and leaving it at 200 would be an arbitrary, undocumented gap between two fields the
 * schema treats identically — not a defect, just an inconsistency worth closing while
 * touching the sibling field. (Security review, fix/intake-gate-copy round 2: the first
 * draft of this comment claimed the dead-end reasoning for both fields, which was true
 * of About You and false of Spouse/Partner.)
 */
export const MAX_EMAIL = shape.aboutYou.shape.email.unwrap().maxLength ?? 254;

export type PersonDraft = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  employer: string;
  title: string;
}

export type ChildDraft = { name: string; dateOfBirth: string; gender: string }
export type IncomeSourceDraft = { description: string; annualAmount: string; notes: string }
/**
 * `rowId` names the row to the statements uploaded into it (crossmarc/steward-app#2185). It is
 * `''` only on the factories' template (`newCashAccount`). A row the person adds
 * (`addedCashAccount`) and a row loaded from a saved draft (`reviveRows`) always has a random id,
 * which it keeps through reorders and removals of OTHER rows. It is not an answer:
 * {@link isBlankRow} ignores it, so a row holding only its id is blank.
 */
export type CashAccountDraft = { rowId: string; accountType: string; institution: string; balance: string }
export type InvestmentAccountDraft = { rowId: string; accountType: string; institution: string; owner: string; balance: string }
export type PropertyDraft = {
  propertyType: string; description: string; estimatedValue: string; mortgageBalance: string; mortgageRate: string;
  // Which of these the wizard shows depends on `propertyType` (INTAKE_PROPERTY_FIELDS_BY_TYPE).
  purchasePrice: string; purchaseYear: string; monthlyRent: string; occupancy: string; longTermPlan: string;
  improvements: string; businessRentsSpace: string; vacationRentalIncome: string; annualRentalIncome: string;
  landWhatItIs: string; landPlan: string;
}
export type BusinessDraft = {
  name: string; description: string; estimatedValue: string;
  ownershipPercentage: string; lastValuationDate: string; completedBy: string;
  entityType: string; annualRevenue: string; employeeCount: string; ownerPay: string; annualTakeHome: string;
  otherOwners: string; otherOwnersDetails: string; businessHoldings: string; businessHoldingsDetails: string;
  exitThinking: string; buySellAgreement: string; ifSomethingHappened: string;
}
export type OtherAssetDraft = { assetType: string; description: string; estimatedValue: string }
export type DebtDraft = { debtType: string; description: string; balance: string; interestRate: string; monthlyPayment: string }
export type PolicyDraft = { policyType: string; provider: string; coverageAmount: string }
export type TeamMemberDraft = { name: string; firm: string; relationship: string; relationshipQuality: string }
export type OtherAdvisorDraft = { name: string; firm: string; role: string; relationshipQuality: string }

export type GettingStartedDraft = {
  ownsRealEstate: string; ownsBusiness: string; businessHoldsAssets: string; receivesRentalIncome: string;
}

export interface IntakeDraft {
  gettingStarted: GettingStartedDraft;
  aboutYou: PersonDraft;
  spousePartner: { hasSpousePartner: boolean; spouse: PersonDraft; children: ChildDraft[] };
  incomeAndGoals: {
    incomeSources: IncomeSourceDraft[];
    monthlyExpenses: string;
    targetAnnualIncome: string;
    notes: string;
  };
  cashAccounts: CashAccountDraft[];
  investmentAccounts: { accounts: InvestmentAccountDraft[]; statementsNote: string };
  realEstate: PropertyDraft[];
  businessInterests: BusinessDraft[];
  otherAssetsAndDebt: { otherAssets: OtherAssetDraft[]; debts: DebtDraft[] };
  insuranceAndEstate: { policies: PolicyDraft[]; estateDocuments: string[]; lastReviewed: string };
  currentTeam: {
    cpa: TeamMemberDraft;
    attorney: TeamMemberDraft;
    otherAdvisors: OtherAdvisorDraft[];
    notes: string;
  };
  investmentProfile: {
    assetJobs: string[];
    incomeTimeline: string;
    marketFamiliarity: string;
    riskConcern: string;
    downturnResponse: string;
    upcomingCashNeeds: string;
    philosophy: string;
  };
  anythingElse: { notes: string };
}

/** The 13 sections, in the order the schema declares them. */
export const INTAKE_SECTION_KEYS = [
  'gettingStarted',
  'aboutYou',
  'spousePartner',
  'incomeAndGoals',
  'cashAccounts',
  'investmentAccounts',
  'realEstate',
  'businessInterests',
  'otherAssetsAndDebt',
  'insuranceAndEstate',
  'currentTeam',
  'investmentProfile',
  'anythingElse',
] as const;

export type IntakeSectionKey = (typeof INTAKE_SECTION_KEYS)[number];

// New-row factories: one per repeatable list, shared by the "+ Add" buttons and by
// the revival below (which uses them as the shape template).
export const newGettingStarted = (): GettingStartedDraft => ({
  ownsRealEstate: '', ownsBusiness: '', businessHoldsAssets: '', receivesRentalIncome: '',
});
export const newPerson = (): PersonDraft => ({ firstName: '', lastName: '', email: '', phone: '', employer: '', title: '' });
export const newChild = (): ChildDraft => ({ name: '', dateOfBirth: '', gender: '' });
export const newIncomeSource = (): IncomeSourceDraft => ({ description: '', annualAmount: '', notes: '' });
export const newCashAccount = (): CashAccountDraft => ({ rowId: '', accountType: '', institution: '', balance: '' });
export const newInvestmentAccount = (): InvestmentAccountDraft => ({ rowId: '', accountType: '', institution: '', owner: '', balance: '' });
export const newProperty = (): PropertyDraft => ({
  propertyType: '', description: '', estimatedValue: '', mortgageBalance: '', mortgageRate: '',
  purchasePrice: '', purchaseYear: '', monthlyRent: '', occupancy: '', longTermPlan: '',
  improvements: '', businessRentsSpace: '', vacationRentalIncome: '', annualRentalIncome: '',
  landWhatItIs: '', landPlan: '',
});
export const newBusiness = (): BusinessDraft => ({
  name: '', description: '', estimatedValue: '',
  ownershipPercentage: '', lastValuationDate: '', completedBy: '',
  entityType: '', annualRevenue: '', employeeCount: '', ownerPay: '', annualTakeHome: '',
  otherOwners: '', otherOwnersDetails: '', businessHoldings: '', businessHoldingsDetails: '',
  exitThinking: '', buySellAgreement: '', ifSomethingHappened: '',
});

// The "which fields does this row ask" predicates live in steward-types, shared with the
// advisor's document and the prefill; re-exported so wizard code keeps importing them here.
export { propertyAsks, asksOtherOwnersDetails, asksBusinessHoldingsDetails };
export const newOtherAsset = (): OtherAssetDraft => ({ assetType: '', description: '', estimatedValue: '' });
export const newDebt = (): DebtDraft => ({ debtType: '', description: '', balance: '', interestRate: '', monthlyPayment: '' });
export const newPolicy = (): PolicyDraft => ({ policyType: '', provider: '', coverageAmount: '' });
export const newTeamMember = (): TeamMemberDraft => ({ name: '', firm: '', relationship: '', relationshipQuality: '' });
export const newOtherAdvisor = (): OtherAdvisorDraft => ({ name: '', firm: '', role: '', relationshipQuality: '' });

export function emptyDraft(): IntakeDraft {
  return {
    gettingStarted: newGettingStarted(),
    aboutYou: newPerson(),
    spousePartner: { hasSpousePartner: false, spouse: newPerson(), children: [] },
    incomeAndGoals: { incomeSources: [], monthlyExpenses: '', targetAnnualIncome: '', notes: '' },
    cashAccounts: [],
    investmentAccounts: { accounts: [], statementsNote: '' },
    realEstate: [],
    businessInterests: [],
    otherAssetsAndDebt: { otherAssets: [], debts: [] },
    insuranceAndEstate: { policies: [], estateDocuments: [], lastReviewed: '' },
    currentTeam: { cpa: newTeamMember(), attorney: newTeamMember(), otherAdvisors: [], notes: '' },
    investmentProfile: {
      assetJobs: [], incomeTimeline: '', marketFamiliarity: '', riskConcern: '',
      downturnResponse: '', upcomingCashNeeds: '', philosophy: '',
    },
    anythingElse: { notes: '' },
  };
}

// ---------------------------------------------------------------------------
// draft → submission
// ---------------------------------------------------------------------------

/** An optional answer: blank means "not answered", so the key is left off entirely. */
const opt = (v: string): string | undefined => (v.trim() === '' ? undefined : v.trim());

/** Drops `undefined` values, so an unanswered optional field is absent, not empty. */
function compact<T extends Record<string, unknown>>(o: T): T {
  return Object.fromEntries(Object.entries(o).filter(([, v]) => v !== undefined)) as T;
}

/** A row the client added and never filled in. Dropped rather than rejected. */
export function isBlankRow(row: Record<string, string>): boolean {
  // `rowId` is a handle for the row's statements, not something the person typed (#2185).
  return Object.entries(row).every(([k, v]) => k === 'rowId' || v.trim() === '');
}

/** A cash or investment row as the form adds it: blank, with the id its statements will name (#2185). */
export const addedCashAccount = (): CashAccountDraft => ({ ...newCashAccount(), rowId: newRowId() });
export const addedInvestmentAccount = (): InvestmentAccountDraft => ({ ...newInvestmentAccount(), rowId: newRowId() });

/** A fresh row id: 32 hex characters, which {@link IntakeRowIdSchema} admits. */
export function newRowId(): string {
  const bytes = new Uint8Array(16);
  globalThis.crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

/**
 * A property row with nothing in it that its kind would submit. A row holding only answers its
 * kind hides (rent, then the kind changed to Land) would otherwise survive pruning and go out as
 * `{}`, so this asks the same {@link propertyAsks} that {@link toSubmission} sends from.
 */
const isBlankProperty = (p: PropertyDraft): boolean =>
  (Object.keys(p) as (keyof PropertyDraft)[]).every((k) => !propertyAsks(p.propertyType, k) || p[k].trim() === '');

/** The business counterpart of {@link isBlankProperty}: the two follow-ups count only when asked. */
const isBlankBusiness = (b: BusinessDraft): boolean =>
  (Object.keys(b) as (keyof BusinessDraft)[]).every((k) =>
    b[k].trim() === ''
    || (k === 'otherOwnersDetails' && !asksOtherOwnersDetails(b.otherOwners))
    || (k === 'businessHoldingsDetails' && !asksBusinessHoldingsDetails(b.businessHoldings)));

const filled = <T extends Record<string, string>>(rows: T[]): T[] => rows.filter((r) => !isBlankRow(r));

/**
 * Drops rows the client added and left entirely empty, from the DRAFT itself.
 *
 * It has to happen here rather than inside `toSubmission`, and the reason is the
 * error paths: zod reports `accounts.1.accountType` against the array it was handed,
 * so if the submission had dropped a blank row the wizard would paint that message on
 * the wrong card. Pruning the draft first keeps the two arrays index-for-index
 * identical, and an empty card disappearing when you press Next is honest about what
 * was going to be sent anyway.
 */
export function pruneBlankRows(d: IntakeDraft, statements: readonly { rowId?: string }[] = []): IntakeDraft {
  // A row with nothing typed but a statement filed under it is kept (autosave passes the
  // statements), so the file does not lose its account on a reload. Submit and validation pass
  // none, and a blank row goes: its files then show at the section level (#2185).
  const referenced = new Set(statements.flatMap((s) => (typeof s.rowId === 'string' ? [s.rowId] : [])));
  const filledOrFiled = <T extends Record<string, string>>(rows: T[]): T[] =>
    rows.filter((r) => !isBlankRow(r) || (r['rowId'] !== undefined && r['rowId'] !== '' && referenced.has(r['rowId'])));
  return {
    ...d,
    spousePartner: { ...d.spousePartner, children: filled(d.spousePartner.children) },
    incomeAndGoals: { ...d.incomeAndGoals, incomeSources: filled(d.incomeAndGoals.incomeSources) },
    cashAccounts: filledOrFiled(d.cashAccounts),
    investmentAccounts: { ...d.investmentAccounts, accounts: filledOrFiled(d.investmentAccounts.accounts) },
    realEstate: d.realEstate.filter((p) => !isBlankProperty(p)),
    businessInterests: d.businessInterests.filter((b) => !isBlankBusiness(b)),
    otherAssetsAndDebt: {
      otherAssets: filled(d.otherAssetsAndDebt.otherAssets),
      debts: filled(d.otherAssetsAndDebt.debts),
    },
    insuranceAndEstate: { ...d.insuranceAndEstate, policies: filled(d.insuranceAndEstate.policies) },
    currentTeam: { ...d.currentTeam, otherAdvisors: filled(d.currentTeam.otherAdvisors) },
  };
}

function personOut(p: PersonDraft) {
  return compact({
    firstName: opt(p.firstName),
    lastName: opt(p.lastName),
    email: opt(p.email),
    phone: opt(p.phone),
    employer: opt(p.employer),
    title: opt(p.title),
  });
}

/**
 * The wire body for `POST /intake/{firmSlug}`. Expects an already-pruned draft (see
 * {@link pruneBlankRows}) so its rows line up with the ones on screen. The result is
 * a candidate, not a validated submission: the only thing entitled to call it valid
 * is `IntakeSubmissionSchema` (here, and again on the façade).
 *
 * The honeypot always rides along, filled or not — a bot that fills it gets the same
 * 202 as everyone else, and a field that only appears sometimes is a tell.
 */
export function toSubmission(draft: IntakeDraft, honeypot: string): Record<string, unknown> {
  const d = draft;
  return {
    [INTAKE_HONEYPOT_FIELD]: honeypot,

    gettingStarted: compact({
      ownsRealEstate: opt(d.gettingStarted.ownsRealEstate),
      ownsBusiness: opt(d.gettingStarted.ownsBusiness),
      businessHoldsAssets: opt(d.gettingStarted.businessHoldsAssets),
      receivesRentalIncome: opt(d.gettingStarted.receivesRentalIncome),
    }),

    aboutYou: personOut(d.aboutYou),

    spousePartner: compact({
      hasSpousePartner: d.spousePartner.hasSpousePartner,
      spouse: d.spousePartner.hasSpousePartner ? personOut(d.spousePartner.spouse) : undefined,
      children: d.spousePartner.children.map((c) => compact({
        name: opt(c.name),
        dateOfBirth: opt(c.dateOfBirth),
        gender: opt(c.gender),
      })),
    }),

    incomeAndGoals: compact({
      incomeSources: d.incomeAndGoals.incomeSources.map((s) => compact({
        description: opt(s.description),
        annualAmount: opt(s.annualAmount),
        notes: opt(s.notes),
      })),
      monthlyExpenses: opt(d.incomeAndGoals.monthlyExpenses),
      targetAnnualIncome: opt(d.incomeAndGoals.targetAnnualIncome),
      notes: opt(d.incomeAndGoals.notes),
    }),

    cashAccounts: d.cashAccounts.map((a) => compact({
      rowId: opt(a.rowId),
      accountType: opt(a.accountType),
      institution: opt(a.institution),
      balance: opt(a.balance),
    })),

    investmentAccounts: compact({
      accounts: d.investmentAccounts.accounts.map((a) => compact({
        rowId: opt(a.rowId),
        accountType: opt(a.accountType),
        institution: opt(a.institution),
        owner: opt(a.owner),
        balance: opt(a.balance),
      })),
      statementsNote: opt(d.investmentAccounts.statementsNote),
    }),

    realEstate: d.realEstate.map((p) => {
      const asked = (field: keyof PropertyDraft) => (propertyAsks(p.propertyType, field) ? opt(p[field]) : undefined);
      return compact({
      propertyType: opt(p.propertyType),
      description: opt(p.description),
      estimatedValue: opt(p.estimatedValue),
      mortgageBalance: asked('mortgageBalance'),
      mortgageRate: asked('mortgageRate'),
      purchasePrice: asked('purchasePrice'),
      purchaseYear: asked('purchaseYear'),
      monthlyRent: asked('monthlyRent'),
      occupancy: asked('occupancy'),
      longTermPlan: asked('longTermPlan'),
      improvements: asked('improvements'),
      businessRentsSpace: asked('businessRentsSpace'),
      vacationRentalIncome: asked('vacationRentalIncome'),
      annualRentalIncome: asked('annualRentalIncome'),
      landWhatItIs: asked('landWhatItIs'),
      landPlan: asked('landPlan'),
      });
    }),

    businessInterests: d.businessInterests.map((b) => compact({
      name: opt(b.name),
      description: opt(b.description),
      estimatedValue: opt(b.estimatedValue),
      ownershipPercentage: opt(b.ownershipPercentage),
      lastValuationDate: opt(b.lastValuationDate),
      completedBy: opt(b.completedBy),
      entityType: opt(b.entityType),
      annualRevenue: opt(b.annualRevenue),
      employeeCount: opt(b.employeeCount),
      ownerPay: opt(b.ownerPay),
      annualTakeHome: opt(b.annualTakeHome),
      otherOwners: opt(b.otherOwners),
      otherOwnersDetails: asksOtherOwnersDetails(b.otherOwners) ? opt(b.otherOwnersDetails) : undefined,
      businessHoldings: opt(b.businessHoldings),
      businessHoldingsDetails: asksBusinessHoldingsDetails(b.businessHoldings) ? opt(b.businessHoldingsDetails) : undefined,
      exitThinking: opt(b.exitThinking),
      buySellAgreement: opt(b.buySellAgreement),
      ifSomethingHappened: opt(b.ifSomethingHappened),
    })),

    otherAssetsAndDebt: {
      otherAssets: d.otherAssetsAndDebt.otherAssets.map((a) => compact({
        assetType: opt(a.assetType),
        description: opt(a.description),
        estimatedValue: opt(a.estimatedValue),
      })),
      debts: d.otherAssetsAndDebt.debts.map((l) => compact({
        debtType: opt(l.debtType),
        description: opt(l.description),
        balance: opt(l.balance),
        interestRate: opt(l.interestRate),
        monthlyPayment: opt(l.monthlyPayment),
      })),
    },

    insuranceAndEstate: compact({
      policies: d.insuranceAndEstate.policies.map((p) => compact({
        policyType: opt(p.policyType),
        provider: opt(p.provider),
        coverageAmount: opt(p.coverageAmount),
      })),
      estateDocuments: d.insuranceAndEstate.estateDocuments,
      lastReviewed: opt(d.insuranceAndEstate.lastReviewed),
    }),

    currentTeam: compact({
      cpa: isBlankRow(d.currentTeam.cpa) ? undefined : compact({
        name: opt(d.currentTeam.cpa.name),
        firm: opt(d.currentTeam.cpa.firm),
        relationship: opt(d.currentTeam.cpa.relationship),
        relationshipQuality: opt(d.currentTeam.cpa.relationshipQuality),
      }),
      attorney: isBlankRow(d.currentTeam.attorney) ? undefined : compact({
        name: opt(d.currentTeam.attorney.name),
        firm: opt(d.currentTeam.attorney.firm),
        relationship: opt(d.currentTeam.attorney.relationship),
        relationshipQuality: opt(d.currentTeam.attorney.relationshipQuality),
      }),
      otherAdvisors: d.currentTeam.otherAdvisors.map((a) => compact({
        name: opt(a.name),
        firm: opt(a.firm),
        role: opt(a.role),
        relationshipQuality: opt(a.relationshipQuality),
      })),
      notes: opt(d.currentTeam.notes),
    }),

    investmentProfile: compact({
      assetJobs: d.investmentProfile.assetJobs,
      incomeTimeline: opt(d.investmentProfile.incomeTimeline),
      marketFamiliarity: opt(d.investmentProfile.marketFamiliarity),
      riskConcern: opt(d.investmentProfile.riskConcern),
      downturnResponse: opt(d.investmentProfile.downturnResponse),
      upcomingCashNeeds: opt(d.investmentProfile.upcomingCashNeeds),
      philosophy: opt(d.investmentProfile.philosophy),
    }),

    anythingElse: compact({
      notes: opt(d.anythingElse.notes),
    }),
  };
}

/**
 * The wire body for `PUT /intake/{firmSlug}/draft` — the same conversion
 * {@link toSubmission} makes, with the honeypot removed (docs/29 §The claim,
 * steward-app#1012 PR 6).
 *
 * 🚨 The removal is not tidiness. `IntakeDraftSectionsSchema`
 * (`packages/steward-types/src/intakeDraft.ts`) `.omit()`s the honeypot, and every level
 * of it is a `strictObject` — so `website` is an UNDECLARED key on the save path and a
 * body carrying one is a 400 for every caller, on every autosave. The claim and
 * submission routes accept-and-drop the field instead, because on those it is a live
 * anti-spam control whose whole value is answering identically.
 *
 * Expects an already-pruned draft, for the reason {@link toSubmission} gives.
 */
export function toDraftSections(draft: IntakeDraft): Record<string, unknown> {
  const { [INTAKE_HONEYPOT_FIELD]: _honeypot, ...sections } = toSubmission(draft, '');
  return sections;
}

/** The slice of a zod schema {@link draftPayload} uses, so this file does not import zod itself. */
type SectionSchema = {
  safeParse(value: unknown):
    | { success: true }
    | { success: false; error: { issues: ReadonlyArray<{ path: ReadonlyArray<PropertyKey> }> } };
};

/**
 * The `sections` of a draft save (crossmarc/steward-app#2421): the draft as
 * {@link toDraftSections} converts it, with every answer the server would refuse HELD BACK, so
 * one invalid answer cannot stop every other answer from saving.
 *
 * Why: the save route parses `sections` with `IntakeDraftSectionsSchema`, strictly, and refuses
 * the whole body over one bad field. Before this, a spouse email of "jane@example" made every
 * autosave a 400 ("Couldn't save. Try again"), and every OTHER answer typed after it went
 * unsaved too, for as long as the bad one stayed.
 *
 * The check is that same schema, `IntakeDraftSectionsSchema` from `@steward-app/steward-types`,
 * section by section, so this cannot hold back something the server would take, or let through
 * something it would refuse, without the two drifting apart in the shared package first. The
 * form's own section check (`validateSection`) reads the same section schemas through
 * `IntakeSubmissionSchema`; the draft schema is that one with each section made optional.
 *
 * For each section the schema refuses, each refused answer is:
 *  - held at the value `lastAccepted` has at the same path (the last copy the server took), or
 *  - left out, when there is none, or when the path runs through a row (`children.1.name`): a
 *    row's position is not its identity, so a row removed above it would put another row's old
 *    answer in its place. Left out, the row keeps every other answer.
 * If the section still fails after that (the held value is itself refused, say after the schema
 * tightened), each answer still refused is left out. If it fails even then (a refusal with no
 * field path, or one on a row as a whole), the whole section is sent as `lastAccepted` has it,
 * whether or not that passes, and is left out only when `lastAccepted` has no copy of it: the
 * writer replaces `sections` whole, so a section left out is a section deleted.
 *
 * What this does NOT do is decide what may be SENT to the firm: `send()` refuses while any
 * answer is invalid, so by the time a send's save runs nothing is held back and the payload is
 * the draft on screen. Every save path goes through this one function (`useSignedInDraft.ts`,
 * `payloadFor`), so an autosave, a section move, the save before a send and the keepalive write
 * on the way out cannot disagree about it.
 */
export function draftPayload(
  draft: IntakeDraft,
  statements: readonly { rowId?: string }[],
  lastAccepted: Record<string, unknown> | null,
): Record<string, unknown> {
  const sections = toDraftSections(pruneBlankRows(draft, statements));
  const shape = IntakeDraftSectionsSchema.shape as unknown as Record<string, SectionSchema>;
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(sections)) {
    const schema = shape[key];
    // Every key `toDraftSections` writes is a section of the schema. One that is not would be
    // refused by the server whatever this did, so it is sent as it is and the save fails loudly.
    if (!schema) { out[key] = value; continue; }
    const first = schema.safeParse(value);
    if (first.success) { out[key] = value; continue; }

    const accepted = lastAccepted?.[key];
    let held: unknown = JSON.parse(JSON.stringify(value));
    for (const issue of first.error.issues) {
      const path = issue.path;
      if (path.length === 0) { held = undefined; break; }
      const prior = path.some((step) => typeof step === 'number') ? undefined : valueAt(accepted, path);
      held = prior === undefined ? withoutPath(held, path) : withPath(held, path, prior);
    }
    if (held !== undefined) {
      const second = schema.safeParse(held);
      if (second.success) { out[key] = held; continue; }
      // The held value can itself be refused: a stored answer the schema has since tightened
      // against. Those answers are left out instead.
      for (const issue of second.error.issues) {
        if (issue.path.length === 0) { held = undefined; break; }
        held = withoutPath(held, issue.path);
      }
      if (held !== undefined && schema.safeParse(held).success) { out[key] = held; continue; }
    }
    // 🚨 Never leave out a section the server has. The writer REPLACES `sections` whole
    // (`services/intake-writer/src/draft.ts`), so a key missing here deletes every stored
    // answer in that section. The last accepted copy goes as it is, even when it no longer
    // passes: a refused save ("Couldn't save") is loud, a silently emptied section is not.
    if (accepted !== undefined) out[key] = accepted;
  }
  return out;
}

function valueAt(root: unknown, path: ReadonlyArray<PropertyKey>): unknown {
  let at: unknown = root;
  for (const step of path) {
    if (typeof at !== 'object' || at === null) return undefined;
    at = (at as Record<PropertyKey, unknown>)[step];
  }
  return at;
}

/** `root` with the value at `path` replaced, in place on a copy the caller owns. */
function withPath(root: unknown, path: ReadonlyArray<PropertyKey>, value: unknown): unknown {
  const parent = valueAt(root, path.slice(0, -1));
  if (typeof parent !== 'object' || parent === null) return root;
  (parent as Record<PropertyKey, unknown>)[path[path.length - 1]!] = value;
  return root;
}

/** `root` without the value at `path`, in place on a copy the caller owns. */
function withoutPath(root: unknown, path: ReadonlyArray<PropertyKey>): unknown {
  const parent = valueAt(root, path.slice(0, -1));
  if (typeof parent !== 'object' || parent === null || Array.isArray(parent)) return root;
  delete (parent as Record<PropertyKey, unknown>)[path[path.length - 1]!];
  return root;
}

// ---------------------------------------------------------------------------
// validation (the schema answers; this only phrases the answer)
// ---------------------------------------------------------------------------

/** Field path within a section, e.g. `accounts.0.institution`, to its message. */
export type SectionErrors = Record<string, string>;

/**
 * crossmarc/steward-app#1752 (Matt's ruling, QA 2026-09-23): "blocking Next is fine when
 * something on the page is actually wrong, but any block must tell the person exactly
 * what to fix. A vague statement is not acceptable, and neither is a rule about the form
 * as a whole." `'This one is needed before you continue.'` used to answer both
 * `'invalid_type'` and `'too_small'` here — but nothing on this form is required (every
 * field in `IntakeSubmissionSchema` is `.optional()`; see that schema's own docblock), so
 * the message was never true of anything a person could actually be shown: the only way
 * either code fires is a type/shape mismatch (e.g. a number field handed a string), which
 * is "wrong", not "missing". Retired in favour of falling through to the same generic
 * message every other unmapped issue code already uses.
 */
function messageFor(issue: { code: string; message: string }): string {
  switch (issue.code) {
    case 'invalid_value':
      return 'Please choose one of the options.';
    case 'invalid_format':
      return issue.message.toLowerCase().includes('email')
        ? 'Please use an email address, like name@example.com.'
        : 'Please check the format of this answer.';
    case 'too_big':
      return 'That is longer than this form can accept. Please shorten it.';
    default:
      return 'Please take another look at this answer.';
  }
}

/**
 * Strips `email` from `aboutYou` before it reaches the schema, for an invited session
 * only (crossmarc/steward-app#1752, item 2). Mirrors `draftSync.ts`'s
 * `sectionsForConfirmation`, which strips the same field from the same case for the same
 * reason: on an invited draft the address is the writer's to stamp, never something the
 * client typed or can correct, so it must never be a reason a field-level validation error is raised — that
 * would be "the banner with nothing marked" (Matt's phrase), since no rendered input
 * exists to attach the error to.
 *
 * Defensive rather than the confirmed trigger of the bug report: every write path already
 * validates the invited address as `z.email().max(254)` before a row is stored
 * (`services/steward-app-api/src/handlers/intakeInvitation.ts`, `sponsoredSetup.ts`; see
 * the #1752 correction comment, 2026-09-23), so a stamped address can't actually fail this
 * schema through the product today. Kept anyway as cheap insurance for a row written
 * outside the façade.
 */
function aboutYouForValidation(aboutYou: unknown, invitedSession: boolean): unknown {
  return invitedSession ? aboutYouWithoutInvitedEmail(aboutYou) : aboutYou;
}

/**
 * Same stripping as {@link aboutYouForValidation}, applied to a whole submission rather
 * than one section, for the call site (`IntakeWizard`'s `send()` emptiness check) that
 * hands the whole submission straight to `IntakeSubmissionSchema.safeParse` rather than
 * going through `validateSection` / `firstInvalidSection`. Without this, a stamped invited
 * address that failed FORMAT would fail that whole-schema parse for the wrong reason, and
 * the caller would say "this form is empty" over content that plainly is not, on the
 * exact shape of Matt's bug report (crossmarc/steward-app#1752). The stripped object is
 * used only to decide whether the submission counts as answered; the unstripped
 * `submission` is still what gets saved and sent.
 *
 * 🚨 **Deliberate second consequence, not just the format fix above: an invited draft
 * with a stamped email and nothing else now counts as EMPTY too**, the same as it would
 * for an uninvited one. Before this function existed, a wholly blank invited submission
 * still passed `IntakeSubmissionSchema`'s own non-empty refine, purely because the
 * stamped `aboutYou.email` string was there to count as "an answer" — an answer the
 * client never gave. Stripping it for this check closes that the same way it closes the
 * format failure above: the field is never something the client can point to, so it must
 * never be why a check comes out either way. See `draft.test.ts`'s
 * "treats a blank invited submission as empty" for the pin.
 *
 * The stripping itself lives in `@steward-app/steward-types`
 * (`submissionWithoutInvitedEmail`), and the intake writer's submit-time parse calls the same
 * function for an invited session (crossmarc/steward-app#1758), so the client and the writer
 * agree on this case. The intake writer's `draft.test.ts` and this file's "parity" test pin it.
 *
 * As of crossmarc/steward-app#2158 (2026-10-07) the signed-in form calls this, and
 * `validateSection` / `firstInvalidSection`, with `invitedSession: false` only: there is no
 * stamped address on that form. The `true` side is kept because `draft.test.ts` holds the
 * browser half of the writer's parity table (#1758), and is exercised only there.
 */
export function submissionForValidation(
  submission: Record<string, unknown>, invitedSession: boolean,
): Record<string, unknown> {
  return invitedSession ? submissionWithoutInvitedEmail(submission) : submission;
}

/**
 * Validates one section against its slice of `IntakeSubmissionSchema`. Keys are the
 * issue path relative to the section, so a field renders its own message without the
 * section having to know anything about zod.
 *
 * `invitedSession` (crossmarc/steward-app#1752) strips `aboutYou.email` first, via
 * {@link aboutYouForValidation} — see that function's own comment for why.
 */
export function validateSection(
  key: IntakeSectionKey, submission: Record<string, unknown>, invitedSession: boolean,
): SectionErrors {
  const value = key === 'aboutYou' ? aboutYouForValidation(submission[key], invitedSession) : submission[key];
  const result = IntakeSubmissionSchema.shape[key].safeParse(value);
  if (result.success) return {};
  const errors: SectionErrors = {};
  for (const issue of result.error.issues) {
    const path = issue.path.join('.');
    if (!(path in errors)) errors[path] = messageFor(issue);
  }
  return errors;
}

/**
 * The whole submission, section by section, so a failed submit can send the client
 * back to the FIRST section that needs another look rather than to a wall of errors.
 * Every section is checked, including one the client's form is not showing, because the whole
 * form is what gets sent (crossmarc/steward-app#2190). Returns null when everything passes.
 */
export function firstInvalidSection(
  submission: Record<string, unknown>, invitedSession: boolean,
): { index: number; key: IntakeSectionKey; errors: SectionErrors } | null {
  for (let index = 0; index < INTAKE_SECTION_KEYS.length; index++) {
    const key = INTAKE_SECTION_KEYS[index]!;
    const errors = validateSection(key, submission, invitedSession);
    if (Object.keys(errors).length > 0) return { index, key, errors };
  }
  return null;
}

/**
 * Encoded size of a string on the wire.
 *
 * Bytes, never string length: the two differ by exactly the non-ASCII characters a
 * real name, address or note is most likely to contain, and the façade counts bytes.
 */
export function encodedByteLength(body: string): number {
  return new TextEncoder().encode(body).length;
}

/** Encoded size of a value once it is JSON. `undefined` serialises to nothing. */
function encodedBytesOf(value: unknown): number {
  return encodedByteLength(JSON.stringify(value) ?? '');
}

export interface OversizeReport {
  /** Encoded size of the body the wizard was about to send. */
  bytes: number;
  /** The façade's pre-parse cap, from the shared package. */
  limit: number;
  /** Sections carrying most of the weight, largest first. Empty when it is spread thin. */
  sections: IntakeSectionKey[];
}

/**
 * Whether this submission is too large to send, and which sections to point at.
 *
 * The two limits in play are not the same number: the schema's own ceiling (every
 * field bounded, every list capped) lands somewhere near 150KB, while the endpoint
 * refuses anything over {@link INTAKE_MAX_BODY_BYTES} on the RAW body, before it parses
 * a byte. So a large but entirely legitimate submission — many accounts, several
 * properties, thoughtful prose in the open questions — passes every field rule and is
 * still rejected, as a bare 400 that deliberately carries no explanation.
 *
 * That leaves this the only place a family can be told anything useful, so it does the
 * arithmetic the endpoint will do (`body` is the exact string that would be sent) and
 * attributes the weight per section. Advisory only: the server's cap is the real
 * boundary and is unchanged by anything here.
 */
export function oversizeReport(
  submission: Record<string, unknown>,
  body: string,
): OversizeReport | null {
  const bytes = encodedByteLength(body);
  if (bytes <= INTAKE_MAX_BODY_BYTES) return null;
  const sections = INTAKE_SECTION_KEYS
    .map((key) => ({ key, bytes: encodedBytesOf(submission[key]) }))
    // A section worth naming carries a real share of the total. Below that the length
    // is spread across the form, and naming two sections at random would send someone
    // trimming in the wrong place.
    .filter((s) => s.bytes >= bytes * 0.15)
    .sort((a, b) => b.bytes - a.bytes)
    .slice(0, 2)
    .map((s) => s.key);
  return { bytes, limit: INTAKE_MAX_BODY_BYTES, sections };
}

// ---------------------------------------------------------------------------
// revival from the server's sections
// ---------------------------------------------------------------------------

/**
 * Copies only same-shaped string fields from a stored row onto a fresh template,
 * truncated to the longest answer the schema accepts anywhere. The inputs enforce
 * their own tighter `maxLength` while a client types, but a hand-edited storage value
 * bypasses that and would otherwise sit in the form as an answer that can only ever be
 * rejected. Trimming it here keeps the draft to something the form could have produced.
 */
function reviveStrings<T extends Record<string, string>>(raw: unknown, template: T): T {
  const out = { ...template };
  const src = (raw ?? {}) as Record<string, unknown>;
  for (const key of Object.keys(template) as (keyof T & string)[]) {
    const value = src[key];
    if (typeof value === 'string') out[key] = value.slice(0, MAX_LONG) as T[keyof T & string];
  }
  return out;
}

function reviveRows<T extends Record<string, string>>(raw: unknown, make: () => T): T[] {
  if (!Array.isArray(raw)) return [];
  const seen = new Set<string>();
  return raw.slice(0, MAX_INTAKE_ROWS).map((row) => {
    const revived = reviveStrings(row, make());
    // A row with no well-formed `rowId` (one saved before rows carried it, or a hand-edited
    // value) is given a fresh one, so every row on screen has an id for its statements and the
    // next save never carries a value the schema refuses (#2185). So is a row whose id an
    // EARLIER row of the same list already has: the first keeps it (and the files named for it),
    // and no two rows share a React key or answer to the same removal.
    if ('rowId' in revived) {
      const id = revived['rowId'];
      if (!INTAKE_ROW_ID_PATTERN.test(id) || seen.has(id)) {
        const fresh = newRowId();
        seen.add(fresh);
        return { ...revived, rowId: fresh };
      }
      seen.add(id);
    }
    return revived;
  });
}

function reviveChoices(raw: unknown, allowed: readonly string[]): string[] {
  if (!Array.isArray(raw)) return [];
  const chosen = raw.filter((v): v is string => typeof v === 'string' && allowed.includes(v));
  return [...new Set(chosen)];
}

const obj = (raw: unknown): Record<string, unknown> =>
  (raw && typeof raw === 'object' && !Array.isArray(raw) ? raw : {}) as Record<string, unknown>;

/**
 * Rebuilds a draft from whatever the server returned, field by field against a fresh
 * empty draft. Anything of the wrong TYPE is dropped, and a key the draft does not have is
 * ignored: a stored draft from an older bundle (or one edited by hand) can cost the client
 * their answers, but it can never put a non-string into a controlled input. It does NOT check
 * a select's value against the schema's options: `reviveStrings` keeps any string, so a
 * value the schema has never heard of can sit in a select (which then shows blank) and is
 * refused by the schema when submitted. Pinned in `draft.test.ts`.
 */
export function reviveDraft(raw: unknown): IntakeDraft {
  const d = obj(raw);
  const empty = emptyDraft();
  const spouse = obj(d['spousePartner']);
  const income = obj(d['incomeAndGoals']);
  const investments = obj(d['investmentAccounts']);
  const otherAssets = obj(d['otherAssetsAndDebt']);
  const insurance = obj(d['insuranceAndEstate']);
  const team = obj(d['currentTeam']);
  const profile = obj(d['investmentProfile']);

  return {
    gettingStarted: reviveStrings(d['gettingStarted'], newGettingStarted()),
    aboutYou: reviveStrings(d['aboutYou'], empty.aboutYou),
    spousePartner: {
      hasSpousePartner: spouse['hasSpousePartner'] === true,
      spouse: reviveStrings(spouse['spouse'], newPerson()),
      children: reviveRows(spouse['children'], newChild),
    },
    incomeAndGoals: {
      incomeSources: reviveRows(income['incomeSources'], newIncomeSource),
      ...reviveStrings(income, { monthlyExpenses: '', targetAnnualIncome: '', notes: '' }),
    },
    cashAccounts: reviveRows(d['cashAccounts'], newCashAccount),
    investmentAccounts: {
      accounts: reviveRows(investments['accounts'], newInvestmentAccount),
      ...reviveStrings(investments, { statementsNote: '' }),
    },
    realEstate: reviveRows(d['realEstate'], newProperty),
    businessInterests: reviveRows(d['businessInterests'], newBusiness),
    otherAssetsAndDebt: {
      otherAssets: reviveRows(otherAssets['otherAssets'], newOtherAsset),
      debts: reviveRows(otherAssets['debts'], newDebt),
    },
    insuranceAndEstate: {
      policies: reviveRows(insurance['policies'], newPolicy),
      estateDocuments: reviveChoices(insurance['estateDocuments'], INTAKE_ESTATE_DOCUMENTS),
      ...reviveStrings(insurance, { lastReviewed: '' }),
    },
    currentTeam: {
      cpa: reviveStrings(team['cpa'], newTeamMember()),
      attorney: reviveStrings(team['attorney'], newTeamMember()),
      otherAdvisors: reviveRows(team['otherAdvisors'], newOtherAdvisor),
      ...reviveStrings(team, { notes: '' }),
    },
    investmentProfile: {
      assetJobs: reviveChoices(profile['assetJobs'], INTAKE_ASSET_JOBS),
      ...reviveStrings(profile, {
        incomeTimeline: '', marketFamiliarity: '', riskConcern: '',
        downturnResponse: '', upcomingCashNeeds: '', philosophy: '',
      }),
    },
    anythingElse: reviveStrings(d['anythingElse'], { notes: '' }),
  };
}
